<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue'
import { useDetectDevice } from '@/composables/device'
import { useRoute, useRouter } from 'vue-router'
import { useNotify } from '@/composables/notify'
import { AGENTS_CONSTANTS } from '@/constants/agents.constants'

import i18n from '@/plugins/i18n'

import * as agentsApi from '@/apis/agents.api'
import LimitsDashboard from '@/components/shared/LimitsDashboard.vue'
import AgentsList from '@/components/agents/AgentsList.vue'
import AgentQrDialog from '@/components/agents/AgentQrDialog.vue'
import AgentCodeDialog from '@/components/agents/AgentCodeDialog.vue'
import AgentsFormDialog from '@/components/agents/AgentsFormDialog.vue'
import { useWebSiteStore } from '@/stores/website.store'

const websiteStore = useWebSiteStore()
const route = useRoute()
const router = useRouter()

const { isMobile } = useDetectDevice()
const { notifySuccess, notifyInfo, notifyError } = useNotify()
const t = i18n.global.t

const pollingInterval = ref(null)
// Dopo uno start il collegamento con WhatsApp si decide in pochi secondi: per quella finestra
// si guarda piu' spesso, poi si torna al ritmo normale. Prima l'app chiedeva di ricaricare
// la pagina a mano, pur avendo gia' un polling attivo.
const fastPollingInterval = ref(null)
const fastPollingTimeout = ref(null)
const FAST_POLLING_MS = 3000
const FAST_POLLING_DURATION_MS = 90000

const stopFastPolling = () => {
  if (fastPollingInterval.value) {
    clearInterval(fastPollingInterval.value)
    fastPollingInterval.value = null
  }
  if (fastPollingTimeout.value) {
    clearTimeout(fastPollingTimeout.value)
    fastPollingTimeout.value = null
  }
}

const startFastPolling = () => {
  stopFastPolling()
  fastPollingInterval.value = setInterval(() => fetchAgentsAsync(true), FAST_POLLING_MS)
  fastPollingTimeout.value = setTimeout(stopFastPolling, FAST_POLLING_DURATION_MS)
}

const limits = computed(() => {
  const data = websiteStore.userLimits
  if (data && data.agents) {
    return data
  }
  return { agents: { current: 0, total: 0 } }
})
const loadingLimits = ref(false)

const agents = computed(() => {
  const list = websiteStore.agents || []

  const priorityMap = {
    'pending': 1,
    'waiting': 2,
    'action_required': 3,
    'active': 4,
    'sleeping': 5,
    'stopped': 6
  }

  const getPriority = (status) => priorityMap[status] || 99

  return [...list].sort((a, b) => {
    const priorityA = getPriority(a.status)
    const priorityB = getPriority(b.status)

    if (priorityA !== priorityB) {
      return priorityA - priorityB
    }

    return (a.name || '').localeCompare(b.name || '')
  })
})
const loadingAgents = ref(false)

const languages = computed(() => websiteStore.agent_languages || [])
const topics = computed(() => websiteStore.agent_topics || [])

const maxPhoneNumberChanges = computed(() => websiteStore.max_phone_number_changes || 0)

// Create / Edit Dialogs
const showCreateDialog = ref(false)
const showEditDialog = ref(false)
const editItemId = ref(null)

// QR Code Dialog
const qrBlobUrl = ref(null)
const qrLoading = ref(false)
const qrDialogOpen = ref(false)
const qrAgent = ref(null)

// Code Dialog
const codeLoading = ref(false)
const codeDialogOpen = ref(false)
const codeAgent = ref(null)
const authCode = ref(null)

watch(
  () => route.query.create,
  (val) => { showCreateDialog.value = !!val }
)
watch(
  () => route.query.edit,
  (val) => {
    showEditDialog.value = !!val
    editItemId.value = val
  }

)

watch(qrDialogOpen, (open) => {
  if (!open && qrBlobUrl.value) {
    URL.revokeObjectURL(qrBlobUrl.value)
    qrBlobUrl.value = null
  }
})

const openCreateDialog = () => {
  const query = { ...route.query, create: '1' }
  router.push({ query })
}

const closeDialog = () => {
  showCreateDialog.value = false
  showEditDialog.value = false
  setTimeout(() => {
    const query = { ...route.query }
    delete query.create
    delete query.edit
    router.replace({ query })
  }, 300)
}

const fetchLimitsAsync = async() => {
  loadingLimits.value = true
  try {
    await websiteStore.fetchUserLimits()
  } finally {
    loadingLimits.value = false
  }
}
const progress = computed(() => {
  if (!limits.value?.agents?.total) return 0
  return Math.min(100, Math.round((limits.value.agents.current / limits.value.agents.total) * 100))
})

// silent: i giri di polling non accendono lo spinner, altrimenti la lista sfarfalla di continuo.
const fetchAgentsAsync = async(silent = false) => {
  if (!silent) loadingAgents.value = true
  try {
    await websiteStore.fetchAgentsAsync()
  } finally {
    if (!silent) loadingAgents.value = false
  }
}

const fetchAgentTopicsAndLanguages = async() => {
  try {
    await websiteStore.fetchAgentTopicsAndLanguages()
  } catch (error) {
    notifyError(t('agents.notifications.errorFetchingTopicsAndLanguages'), error)
  }
}

const fetchMaxPhoneNumberChangesAsync = async() => {
  try {
    await websiteStore.fetchMaxPhoneNumberChanges()
  } catch (error) {
    notifyError(t('agents.notifications.errorFetchingMaxPhoneNumberChanges'), error)
  }
}

const onCreateSaved = async() => {
  closeDialog()
  await fetchLimitsAsync()
  await fetchAgentsAsync()
}

onMounted(async() => {
  await Promise.all([fetchLimitsAsync(), fetchAgentsAsync(), fetchAgentTopicsAndLanguages(), fetchMaxPhoneNumberChangesAsync()])

  // Set up polling to refresh agents every 15 seconds
  pollingInterval.value = setInterval(async() => {
    await fetchAgentsAsync(true)
  }, AGENTS_CONSTANTS.POLLING_INTERVAL_MS)
})

onUnmounted(() => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
  }
  stopFastPolling()
})

const onStart = async(agent) => {
  await agentsApi.startAgentAsync(agent.id)
  notifySuccess(t('agents.notifications.started'))
  notifyInfo(t('agents.notifications.status_info'))
  startFastPolling()
}

const onStop = async(agent) => {
  await agentsApi.stopAgentAsync(agent.id)
  notifySuccess(t('agents.notifications.stopped'))
  notifyInfo(t('agents.notifications.status_info'))
  startFastPolling()
}

// Il Refresh della card: aggiorna i dati senza ricaricare la pagina.
const onReload = async() => {
  await fetchAgentsAsync(true)
}

const onShowQr = async(agent) => {
  qrLoading.value = true
  qrDialogOpen.value = true
  qrAgent.value = agent

  const response = await agentsApi.fetchAgentQrCodeAsync(agent.id)

  qrBlobUrl.value = URL.createObjectURL(response.data)
  qrLoading.value = false
}

const onShowCode = async(agent) => {
  codeLoading.value = true
  codeDialogOpen.value = true
  codeAgent.value = agent
  const res = await agentsApi.getAuthCodeAsync(agent.id, agent.associated_phone_number.number.trim().replace(/^\+/, ''))
  authCode.value = res.data?.result
  codeLoading.value = false
}

const onUploadChats = (agent) => {
  //fire and forget, no success alert
  agentsApi.uploadAgentChatsAsync(agent.id)
  notifyInfo(t('agents.action.UploadChatsStarted'))

  //   const data = response.data || response

  //   if (data) {
  //     notifySuccess(t('agents.action.upload_chats_success', { count: data.imported_count }))
  //   } else {
  //     notifySuccess(t('agents.action.upload_chats_done'))
  //   }
  // } catch (error) {
  //   console.error('Error uploading chats:', error)

  //   const errorMessage = error.response?.data?.error || t('agents.action.upload_chats_error')

  //   notifyError(errorMessage)
  // }
}

const onRefresh = async(agent) => {
  await agentsApi.refreshAgentAsync(agent.id)
}

</script>

<template>
  <q-page
    class="q-pa-md"
    style="margin-bottom: 80px;"
  >
    <div class="q-mb-md">
      <div class="text-h5 text-weight-bold">
        {{ $t('agents.title') }}
      </div>
      <div class="text-subtitle2 text-grey-7">
        {{ $t('agents.subtitle') }}
      </div>
    </div>

    <div class="q-mb-md">
      <q-banner
        v-if="limits.agents.current >= limits.agents.total && limits.agents.total > 0"
        dense
        rounded
        inline-actions
        class="bg-warning text-white"
      >
        <div class="text-body2">
          {{ $t('agents.notifications.maxActiveAgentsExceeded') }}
        </div>
      </q-banner>
    </div>

    <limits-dashboard
      :current="limits.agents.current"
      :total="limits.agents.total"
      :loading="loadingLimits"
      :progress="progress"
      slider_label="agents.limits.usedOfTotal"
    />

    <div
      class="q-py-md"
    >
      <agents-list
        class="q-mt-md"
        :items="agents"
        :loading="loadingAgents"
        :max-phone-number-changes="maxPhoneNumberChanges"
        @start="onStart"
        @stop="onStop"
        @show-qr="onShowQr"
        @upload-chats="onUploadChats"
        @show-code="onShowCode"
        @refresh="onRefresh"
        @reload="onReload"
      />
    </div>

    <AgentQrDialog
      v-model="qrDialogOpen"
      :agent-name="websiteStore.getAgentName(qrAgent?.id)"
      :qr-url="qrBlobUrl"
      :loading="qrLoading"
    />

    <AgentCodeDialog
      v-model="codeDialogOpen"
      :agent-name="websiteStore.getAgentName(codeAgent?.id)"
      :loading="codeLoading"
      :auth-code="authCode"
    />

    <q-fab
      v-if="limits.agents.current < limits.agents.total"
      color="primary"
      glossy
      icon="mdi-plus"
      direction="up"
      class="fab-create"
      @click="openCreateDialog"
    />

    <agents-form-dialog
      v-model="showCreateDialog"
      mode="create"
      :full-screen="isMobile"
      title="agents.create.title"
      :languages="languages"
      :topics="topics"
      @saved="onCreateSaved"
    />

    <agents-form-dialog
      v-model="showEditDialog"
      mode="edit"
      :item-id="editItemId"
      :full-screen="isMobile"
      title="agents.edit.title"
      :languages="languages"
      :topics="topics"
      :max-phone-number-changes="maxPhoneNumberChanges"
      @saved="onCreateSaved"
    />
  </q-page>
</template>

<style scoped>
.fab-create {
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 1;
}

@media (max-width: 768px) {
  .fab-create {
    bottom: 5rem;
  }
}

/* Il tasto "+" passava inosservato, soprattutto da desktop dove la pagina e' larga e vuota.
   Pulsazione lenta + due onde concentriche tipo sasso nell'acqua: si notano senza infastidire.
   Le onde partono dal bordo del bottone e svaniscono espandendosi, sfalsate di 1,2 s.
   Tutto si ferma al passaggio del mouse e rispetta "riduci animazioni". */
.fab-create {
  animation: fab-pulse 2.6s ease-in-out infinite;
}

.fab-create::before,
.fab-create::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: 50%;
  border: 2px solid currentColor;
  color: var(--q-primary);
  opacity: 0;
  animation: fab-ripple 2.4s ease-out infinite;
  pointer-events: none;
}

.fab-create::after {
  animation-delay: 1.2s;
}

.fab-create:hover,
.fab-create:hover::before,
.fab-create:hover::after {
  animation-play-state: paused;
}

@keyframes fab-pulse {
  0%, 100% { transform: scale(1); box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2); }
  50%      { transform: scale(1.06); box-shadow: 0 8px 22px rgba(0, 0, 0, 0.3); }
}

@keyframes fab-ripple {
  0%   { transform: scale(1);   opacity: 0.55; }
  70%  { opacity: 0.12; }
  100% { transform: scale(2.1); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .fab-create,
  .fab-create::before,
  .fab-create::after { animation: none; }
}
</style>
